﻿/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  function relocateProductAccordions() {
    const origin = document.querySelector('[data-product-accordions-origin]');
    const target = document.querySelector('[data-product-accordions-target]');
    if (!origin || !target) return;

    let content =
      target.querySelector('[data-product-accordions-content]') ||
      origin.querySelector('[data-product-accordions-content]');

    if (!content) {
      const backup = origin.querySelector('[data-product-accordions-backup]');
      if (backup) {
        content = backup.cloneNode(true);
        content.hidden = false;
        content.removeAttribute('data-product-accordions-backup');
        content.setAttribute('data-product-accordions-content', '');
      }
    }

    if (!content) return;

    if (!origin.querySelector('[data-product-accordions-backup]')) {
      const backup = content.cloneNode(true);
      backup.removeAttribute('data-product-accordions-content');
      backup.setAttribute('data-product-accordions-backup', '');
      backup.hidden = true;
      origin.appendChild(backup);
    }

    if (content.parentElement !== target) {
      target.appendChild(content);
    }

    origin.classList.add('emma-accordions-relocated');
    document.dispatchEvent(new CustomEvent('emma:accordions-relocated'));
    bindComplementaryAccordions();
  }

  document.addEventListener('DOMContentLoaded', relocateProductAccordions);
  document.addEventListener('shopify:section:load', relocateProductAccordions);
  window.addEventListener('resize', relocateProductAccordions);

  function bindAccordionRerenderRecovery() {
    const form = document.querySelector('form[id^="product-form-main-"]');
    if (!form || form.dataset.emmaAccordionRerenderBound === 'true') return;
    form.dataset.emmaAccordionRerenderBound = 'true';
    form.addEventListener('product:rerender', () => {
      requestAnimationFrame(relocateProductAccordions);
    });
  }

  document.addEventListener('DOMContentLoaded', bindAccordionRerenderRecovery);
  document.addEventListener('shopify:section:load', bindAccordionRerenderRecovery);

  function refreshComplementaryCollapsible(wrapper) {
    const collapsible = wrapper.querySelector('collapsible-content');
    if (!collapsible?.open) return;
    collapsible.style.height = 'auto';
    collapsible.style.visibility = 'visible';
    collapsible.style.overflow = 'visible';
  }

  function syncAccordionBackup() {
    const origin = document.querySelector('[data-product-accordions-origin]');
    const target = document.querySelector('[data-product-accordions-target]');
    const content = target?.querySelector('[data-product-accordions-content]');
    let backup = origin?.querySelector('[data-product-accordions-backup]');
    if (!origin || !content) return;

    const newBackup = content.cloneNode(true);
    newBackup.removeAttribute('data-product-accordions-content');
    newBackup.setAttribute('data-product-accordions-backup', '');
    newBackup.hidden = true;

    if (backup) {
      backup.replaceWith(newBackup);
    } else {
      origin.appendChild(newBackup);
    }
  }

  function removeComplementaryAccordion(wrapper, observer) {
    wrapper.remove();
    observer?.disconnect();
    syncAccordionBackup();
  }

  function bindComplementaryAccordions() {
    document.querySelectorAll('[data-complementary-accordion]').forEach((wrapper) => {
      if (wrapper.dataset.complementaryBound === 'true') return;
      wrapper.dataset.complementaryBound = 'true';

      const observer = new MutationObserver(() => {
        const recommendations = wrapper.querySelector('product-recommendations');
        if (!recommendations) {
          removeComplementaryAccordion(wrapper, observer);
          return;
        }

        if (recommendations.childElementCount > 0) {
          wrapper.hidden = false;
          refreshComplementaryCollapsible(wrapper);
          syncAccordionBackup();
          document.dispatchEvent(new CustomEvent('emma:product-cards-updated'));
        }
      });

      observer.observe(wrapper, { childList: true, subtree: true });

      const recommendations = wrapper.querySelector('product-recommendations');
      if (!recommendations) {
        removeComplementaryAccordion(wrapper, observer);
      } else if (recommendations.childElementCount > 0) {
        wrapper.hidden = false;
        refreshComplementaryCollapsible(wrapper);
        document.dispatchEvent(new CustomEvent('emma:product-cards-updated'));
      }
    });
  }

  document.addEventListener('DOMContentLoaded', bindComplementaryAccordions);
  document.addEventListener('shopify:section:load', bindComplementaryAccordions);

  /**
   * Related / recently-viewed inject <flickity-carousel> via innerHTML after the
   * custom element is already defined. Constructor then runs with 0 children and
   * Flickity boots empty ÃÂ¢ÃÂÃÂ reload once slides exist.
   */
  async function fixAjaxFlickityCarousels(root) {
    const carousels = root.querySelectorAll('flickity-carousel');
    for (const carousel of carousels) {
      if (carousel.dataset.emmaFlickityFixed === 'true') continue;
      // Constructor ran during innerHTML with 0 children; any slides need a reload.
      if (carousel.childElementCount < 1) continue;
      carousel.dataset.emmaFlickityFixed = 'true';
      if (typeof carousel.reload === 'function') {
        try {
          await carousel.reload();
          if (typeof window.emmaUpdateCarouselArrows === 'function') {
            window.emmaUpdateCarouselArrows(carousel.parentElement || document);
          }
        } catch (e) {
          /* ignore ÃÂ¢ÃÂÃÂ carousel may not be ready yet */
        }
      }
    }
  }

  function bindEmmaCarouselSections() {
    document.querySelectorAll('product-recommendations, recently-viewed-products').forEach((root) => {
      if (root.dataset.emmaCarouselBound === 'true') return;
      root.dataset.emmaCarouselBound = 'true';

      const notifyCardsReady = (observer) => {
        if (!root.querySelector('.emma-product-card')) return;
        if (root.dataset.emmaCardsReady === 'true') return;
        root.dataset.emmaCardsReady = 'true';
        if (observer) observer.disconnect();

        fixAjaxFlickityCarousels(root).finally(() => {
          if (typeof window.emmaUpdateCarouselArrows === 'function') {
            window.emmaUpdateCarouselArrows(root);
          }
          document.dispatchEvent(new CustomEvent('emma:product-cards-updated'));
          if (typeof window.emmaInitDeferImages === 'function') {
            window.emmaInitDeferImages();
          }
        });
      };

      const observer = new MutationObserver(() => notifyCardsReady(observer));
      observer.observe(root, { childList: true, subtree: true });
      notifyCardsReady(observer);
    });
  }

  document.addEventListener('DOMContentLoaded', bindEmmaCarouselSections);
  document.addEventListener('shopify:section:load', bindEmmaCarouselSections);

  /* ------------------------------------------------------------------ */
  /* Carousel arrows ÃÂ¢ÃÂÃÂ hide when all slides fit; snappier navigation     */
  /* ------------------------------------------------------------------ */
  (function emmaCarouselArrows() {
    var resizeTimer;

    function getCarousels(root) {
      var scope = root || document;
      return scope.querySelectorAll(
        'flickity-carousel.emma-product-card-row__carousel, flickity-carousel.emma-best-sellers__carousel, .emma-product-card-row flickity-carousel, .emma-best-sellers flickity-carousel'
      );
    }

    async function getFlickity(carousel) {
      if (!carousel || typeof carousel.flickityInstance === 'undefined') return null;
      return carousel.flickityInstance;
    }

    function carouselFitsAll(flickity) {
      if (!flickity || !flickity.cells || flickity.cells.length === 0) return true;
      if (flickity.cells.length === 1) return true;
      var viewport = flickity.size.innerWidth || flickity.size.width;
      if (!viewport || typeof flickity.slideableWidth !== 'number') return false;
      return flickity.slideableWidth <= viewport + 2;
    }

    function setScrollableState(carousel, fitsAll) {
      carousel.classList.toggle('is-not-scrollable', fitsAll);
      var row = carousel.closest('.emma-product-card-row, .emma-best-sellers');
      if (row) row.classList.toggle('is-not-scrollable', fitsAll);
    }

    async function updateCarouselScrollState(carousel) {
      var flickity = await getFlickity(carousel);
      if (!flickity) return;
      setScrollableState(carousel, carouselFitsAll(flickity));
      if (typeof flickity.resize === 'function') flickity.resize();
    }

    async function onArrowClick(event, carousel) {
      var btn = event.target.closest('.flickity-prev-next-button');
      if (!btn || btn.disabled || carousel.classList.contains('is-not-scrollable')) return;

      var flickity = await getFlickity(carousel);
      if (!flickity) return;

      // If Flickity is mid-animation, force the next/prev cell so one click always moves.
      if (flickity.isAnimating) {
        event.preventDefault();
        event.stopImmediatePropagation();
        var nextIndex = btn.classList.contains('next')
          ? flickity.selectedIndex + 1
          : flickity.selectedIndex - 1;
        flickity.select(nextIndex);
      }
    }

    function bindCarousel(carousel) {
      if (carousel.dataset.emmaArrowsBound === 'true') return;
      carousel.dataset.emmaArrowsBound = 'true';

      ['flickity:ready', 'flickity:slide-settled', 'flickity:slide-changed'].forEach(function (evt) {
        carousel.addEventListener(evt, function () {
          updateCarouselScrollState(carousel);
        });
      });

      carousel.addEventListener(
        'click',
        function (event) {
          onArrowClick(event, carousel);
        },
        true
      );
    }

    function init(root) {
      getCarousels(root).forEach(bindCarousel);
      getCarousels(root).forEach(updateCarouselScrollState);
    }

    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target || document);
    });
    document.addEventListener('emma:product-cards-updated', function () {
      init(document);
    });

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        getCarousels(document).forEach(updateCarouselScrollState);
      }, 150);
    });

    // Expose for post-AJAX flickity reload
    window.emmaUpdateCarouselArrows = function (root) {
      init(root || document);
    };
  })();

  /**
   * Own collection/search AJAX pagination + filters.
   * Focal's product-facet _rerender often only updates the URL (fetch aborted /
   * swallowed) so the grid never swaps ÃÂ¢ÃÂÃÂ MutationObserver never fires, images
   * stay deferred, and scroll stays at the old bottom. Handle the section
   * fetch ourselves in capture phase before Focal runs.
   */
  function scrollFacetResultsIntoView() {
    // Land on the collection page header (title), not the product grid ÃÂ¢ÃÂÃÂ
    // so the sticky emma header sits above Face / sort / products as on first load.
    const target =
      document.querySelector('.shopify-section--collection-banner .page-header') ||
      document.querySelector('.shopify-section--collection-banner') ||
      document.querySelector('.page-header') ||
      document.querySelector('#facet-main .product-facet__meta-bar') ||
      document.querySelector('#facet-main');
    if (!target) return;

    document.querySelectorAll('[class*="emma-header__wrap"].is-autohidden').forEach((wrap) => {
      wrap.classList.remove('is-autohidden');
    });

    const header = document.querySelector('.emma-header');
    let offset = 12;
    if (header) {
      const rect = header.getBoundingClientRect();
      offset = (rect.bottom > 0 ? Math.round(rect.bottom) : Math.round(rect.height)) + 12;
    }

    const top = window.scrollY + target.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }

  function notifyFacetCardsUpdated() {
    document.dispatchEvent(new CustomEvent('emma:product-cards-updated'));
    if (typeof window.emmaInitDeferImages === 'function') {
      window.emmaInitDeferImages();
    }
    window.requestAnimationFrame(() => {
      window.setTimeout(scrollFacetResultsIntoView, 50);
    });
  }

  let emmaFacetAbort = null;

  async function emmaRerenderFacet(facet, nextUrl) {
    if (!facet || !nextUrl) return;

    history.replaceState({}, '', nextUrl);

    const main = facet.querySelector('#facet-main');
    const sectionId = facet.getAttribute('section-id');
    if (!main || !sectionId) return;

    if (emmaFacetAbort) emmaFacetAbort.abort();
    emmaFacetAbort = new AbortController();

    if (typeof facet.showLoadingBar === 'function') facet.showLoadingBar();

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        signal: emmaFacetAbort.signal,
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const doc = document.createElement('div');
      doc.innerHTML = html;

      const nextMain =
        doc.querySelector('#facet-main') ||
        doc.querySelector(`#shopify-section-${sectionId} #facet-main`);
      if (!nextMain) return;

      main.innerHTML = nextMain.innerHTML;

      const nextFilters = doc.querySelector('#facet-filters');
      const liveFilters = facet.querySelector('#facet-filters');
      if (nextFilters && liveFilters) {
        liveFilters.innerHTML = nextFilters.innerHTML;
      }

      const toolbarItem = document.querySelector('.mobile-toolbar__item--filters');
      if (toolbarItem) {
        const hasFilters = doc.querySelectorAll('.product-facet__active-list').length > 0;
        toolbarItem.classList.toggle('has-filters', hasFilters);
      }

      notifyFacetCardsUpdated();
    } catch (e) {
      if (e && e.name === 'AbortError') return;
    } finally {
      if (typeof facet.hideLoadingBar === 'function') facet.hideLoadingBar();
    }
  }

  function onEmmaFacetAjaxEvent(event) {
    const facet =
      (event.target && event.target.closest && event.target.closest('product-facet')) ||
      document.querySelector('product-facet');
    if (!facet || !event.detail || !event.detail.url) return;

    event.stopImmediatePropagation();
    emmaRerenderFacet(facet, event.detail.url);
  }

  function onEmmaSortChanged(event) {
    const target = event.target;
    if (!target || !target.matches || !target.matches('[name="sort_by"]')) return;
    if (!target.closest('sort-by-popover')) return;

    const facet = target.closest('product-facet');
    if (!facet) return;

    event.stopImmediatePropagation();

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('sort_by', target.value);
    nextUrl.searchParams.delete('page');

    const popover = target.closest('sort-by-popover');
    if (popover) popover.open = false;

    emmaRerenderFacet(facet, nextUrl.toString());
  }

  function bindEmmaFacetAjax() {
    if (document.documentElement.dataset.emmaFacetAjaxBound === 'true') return;
    document.documentElement.dataset.emmaFacetAjaxBound = 'true';

    document.addEventListener('pagination:page-changed', onEmmaFacetAjaxEvent, true);
    document.addEventListener('facet:criteria-changed', onEmmaFacetAjaxEvent, true);
    document.addEventListener('change', onEmmaSortChanged, true);
  }

  bindEmmaFacetAjax();
  document.addEventListener('DOMContentLoaded', bindEmmaFacetAjax);
  document.addEventListener('shopify:section:load', bindEmmaFacetAjax);

  function emmaFindGrowaveLaunchers() {
    var launchers = [];
    var visit = function (root) {
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll('.gw-rd-popup-widget-launcher, .gw-launcher').forEach(function (el) {
        /* Prefer the fixed wrapper around the button */
        var fixed = el.classList.contains('gw-rd-popup-widget-launcher')
          ? el
          : (el.closest && el.closest('.gw-rd-popup-widget-launcher')) || el;
        if (launchers.indexOf(fixed) === -1) launchers.push(fixed);
      });
      root.querySelectorAll('*').forEach(function (el) {
        if (el.shadowRoot) visit(el.shadowRoot);
      });
    };
    visit(document);
    return launchers;
  }

  function syncStickyAtcOffset(stickyForm) {
    var root = document.documentElement;
    var visible = stickyForm && !stickyForm.hasAttribute('hidden');
    var offsetPx = 0;

    if (visible) {
      offsetPx = Math.ceil(stickyForm.getBoundingClientRect().height) || stickyForm.clientHeight || 0;
      if (offsetPx > 0) offsetPx += 12;
    }

    if (!offsetPx) {
      root.style.removeProperty('--cart-notification-offset');
      root.style.removeProperty('--emma-sticky-atc-offset');
    } else {
      root.style.setProperty('--cart-notification-offset', offsetPx + 'px');
      root.style.setProperty('--emma-sticky-atc-offset', offsetPx + 'px');
    }

    /* Growave Pink Club launcher is position:fixed bottom:40px inside shadow DOM ÃÂ¢ÃÂÃÂ
       wrappers alone do not move it, so set bottom directly on the launcher. */
    var baseBottom = 40;
    emmaFindGrowaveLaunchers().forEach(function (el) {
      if (!offsetPx) {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transition');
      } else {
        el.style.setProperty('transition', 'bottom 0.25s ease-in-out');
        el.style.setProperty('bottom', (baseBottom + offsetPx) + 'px', 'important');
      }
    });

    document.querySelectorAll('.gw-widget-placeholder-BR-wrapper, .gw-widget-placeholder-BL-wrapper').forEach(function (el) {
      if (!offsetPx) {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transition');
      } else {
        el.style.setProperty('transition', 'bottom 0.25s ease-in-out');
        el.style.setProperty('bottom', (10 + offsetPx) + 'px', 'important');
      }
    });
  }

  function bindStickyAtcReserve() {
    var stickyForm = document.querySelector('product-sticky-form.product-sticky-form');
    if (!stickyForm || stickyForm.dataset.emmaReserveBound === 'true') return;
    stickyForm.dataset.emmaReserveBound = 'true';

    var updateOffset = function () { syncStickyAtcOffset(stickyForm); };

    new MutationObserver(updateOffset).observe(stickyForm, {
      attributes: true,
      attributeFilter: ['hidden', 'class', 'style']
    });

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(updateOffset).observe(stickyForm);
    }

    window.addEventListener('resize', updateOffset, { passive: true });

    if (typeof MutationObserver !== 'undefined') {
      var scheduled = null;
      var bodyObserver = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = setTimeout(function () {
          scheduled = null;
          updateOffset();
        }, 100);
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    updateOffset();
    setTimeout(updateOffset, 500);
    setTimeout(updateOffset, 1500);
    setTimeout(updateOffset, 3000);
  }

  document.addEventListener('DOMContentLoaded', bindStickyAtcReserve);
  document.addEventListener('shopify:section:load', bindStickyAtcReserve);

  function bindWishlistEngine() {
    if (document.documentElement.dataset.emmaWishlistEngineBound === 'true') return;
    document.documentElement.dataset.emmaWishlistEngineBound = 'true';

    document.addEventListener('variant:changed', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      document.querySelectorAll('.product-form__quantity-controls .wishlist-engine').forEach(function (node) {
        node.setAttribute('data-variant_id', String(variant.id));
      });
    });
  }

  function getWishlistEngineButton(wrap) {
    if (!wrap) return null;
    return wrap.querySelector('.wishlist-engine-button, .wishlist-engine button, .wishlist-engine a');
  }

  function isWishlistEngineActive(engine) {
    if (!engine) return false;

    if (engine.getAttribute('data-added') === 'true') return true;

    var engineBtn = engine.querySelector('.wishlist-engine-button');
    if (!engineBtn) return false;

    if (engineBtn.getAttribute('aria-pressed') === 'true') return true;
    if (engineBtn.getAttribute('data-added') === 'true') return true;

    return false;
  }

  function setCardWishlistHeartState(heart, isActive) {
    heart.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  function syncCardWishlistHeart(heart) {
    var wrap = heart.closest('.emma-product-card__media');
    if (!wrap) return;

    var engine = wrap.querySelector('.wishlist-engine');
    setCardWishlistHeartState(heart, isWishlistEngineActive(engine));
  }

  function bindCardWishlistHeart(heart) {
    if (heart.dataset.wishlistHeartBound === 'true') return;
    heart.dataset.wishlistHeartBound = 'true';

    var wrap = heart.closest('.emma-product-card__media');
    var engine = wrap && wrap.querySelector('.wishlist-engine');

    heart.addEventListener('click', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();

      var engineBtn = getWishlistEngineButton(wrap);
      if (engineBtn) {
        engineBtn.click();
      } else if (engine) {
        engine.click();
      }

      /* Prevent :focus-within from leaving the card stuck in hover state. */
      heart.blur();
    });

    if (engine) {
      var observer = new MutationObserver(function () {
        syncCardWishlistHeart(heart);
      });

      observer.observe(engine, {
        attributes: true,
        attributeFilter: ['data-added', 'class'],
        subtree: true
      });

      syncCardWishlistHeart(heart);
      setTimeout(function () { syncCardWishlistHeart(heart); }, 500);
      setTimeout(function () { syncCardWishlistHeart(heart); }, 2000);
    }
  }

  function bindCardWishlistHearts() {
    document.querySelectorAll('[data-wishlist-heart]').forEach(bindCardWishlistHeart);
  }

  window.EmmaWishlistCards = {
    syncHeart: syncCardWishlistHeart,
    bind: bindCardWishlistHearts
  };

  document.addEventListener('DOMContentLoaded', bindWishlistEngine);
  document.addEventListener('shopify:section:load', bindWishlistEngine);
  document.addEventListener('DOMContentLoaded', bindCardWishlistHearts);
  document.addEventListener('shopify:section:load', bindCardWishlistHearts);
  document.addEventListener('emma:product-cards-updated', bindCardWishlistHearts);

  /* Shopify Forms ÃÂ¢ÃÂÃÂ 2-col fields inside shadow DOM */
  var B2B_FORMS_LAYOUT_CSS =
    '[class*="_formContainer"],[class*="_inline"],[class*="_gridItemContent"]{' +
    'overflow:visible!important}' +
    'form{display:flex!important;flex-direction:row!important;flex-wrap:wrap!important;' +
    'column-gap:16px!important;row-gap:12px!important;width:100%!important;max-width:100%!important;' +
    'padding-inline:3px!important;box-sizing:border-box!important}' +
    'form>*{box-sizing:border-box!important;flex:0 0 calc(50% - 8px)!important;' +
    'max-width:calc(50% - 8px)!important;width:calc(50% - 8px)!important;margin-block:0!important;' +
    'min-width:0!important}' +
    'form>div:has(shop-lead-capture){display:none!important}' +
    'form>div:has(input[type="tel"]),form>div:has(input[name="phone_number"]),' +
    'form>div:has(input#phone_number),form>button{' +
    'flex:0 0 100%!important;max-width:100%!important;width:100%!important}' +
    '@media (max-width:740px){form{padding-inline:0!important}' +
    'form>*{flex:0 0 100%!important;max-width:100%!important;width:100%!important}}';

  var BRAND_FORMS_COL_GAP = '48px';
  var BRAND_FORMS_FIELD_W = 'calc((100% - ' + BRAND_FORMS_COL_GAP + ') / 2)';
  var BRAND_FORMS_LAYOUT_CSS =
    ':host{--inline-container-max-width:100%!important;--container-max-w:100%!important;' +
    '--inline-form-alignment:start!important;--app-embed-padding-x:0px!important;' +
    '--forms-padding-left:0px!important;--forms-padding-right:0px!important;' +
    '--spacing-size-x:0px!important}' +
    '[class*="_formContainer"],[class*="_inline"],[class*="_gridItemContent"],' +
    '[data-sizing="form-wrapper"]{overflow:visible!important;max-width:100%!important;width:100%!important;' +
    'padding-inline:0!important;margin-inline:0!important}' +
    '[class*="_formHeader"]{text-align:start!important;padding-inline:0!important}' +
    'form{display:flex!important;flex-flow:row wrap!important;align-items:flex-start!important;' +
    'column-gap:' + BRAND_FORMS_COL_GAP + '!important;row-gap:14px!important;' +
    'width:100%!important;max-width:100%!important;padding-inline:0!important;box-sizing:border-box!important}' +
    'form>*{box-sizing:border-box!important;flex:0 0 ' + BRAND_FORMS_FIELD_W + '!important;' +
    'width:' + BRAND_FORMS_FIELD_W + '!important;max-width:' + BRAND_FORMS_FIELD_W + '!important;' +
    'min-width:0!important;margin-block:0!important}' +
    'form>div:has(shop-lead-capture){display:none!important}' +
    'fieldset>legend[class*="_radioLegend"],fieldset>legend[class*="_checkboxLegend"]{' +
    'font-weight:700!important}' +
    '@media (max-width:740px){form{column-gap:0!important}' +
    'form>*{flex:0 0 100%!important;width:100%!important;max-width:100%!important}}';

  var EMMA_FORMS_LAYOUT = {
    'forms-root-1080004': B2B_FORMS_LAYOUT_CSS,
    'forms-root-1162752': BRAND_FORMS_LAYOUT_CSS
  };

  function emmaFormsLayoutCss(embed) {
    var host = embed && embed.closest('[data-forms-id]');
    var id = host ? host.getAttribute('data-forms-id') || '' : '';
    if (id && EMMA_FORMS_LAYOUT[id]) return EMMA_FORMS_LAYOUT[id];
    if (id.indexOf('1162752') !== -1) return BRAND_FORMS_LAYOUT_CSS;
    if (document.querySelector('.emma-main-page--brand-presentation')) return BRAND_FORMS_LAYOUT_CSS;
    return '';
  }

  function injectEmmaFormsLayout(embed) {
    if (!embed || !embed.shadowRoot) return;
    var css = emmaFormsLayoutCss(embed);
    if (!css) return;

    var root = embed.shadowRoot;
    if (!root.querySelector('form')) return;

    var style = root.getElementById('emma-forms-layout');
    if (!style) {
      style = document.createElement('style');
      style.id = 'emma-forms-layout';
    }
    if (style.textContent !== css) {
      style.textContent = css;
    }
    /* Keep last in the shadow tree so Shopify Forms app CSS cannot override 2-col. */
    if (style.parentNode !== root || style.nextSibling) {
      root.appendChild(style);
    }
  }

  function bindEmmaFormsLayout() {
    document.querySelectorAll('[data-forms-id] shopify-forms-embed').forEach(function (embed) {
      if (!emmaFormsLayoutCss(embed)) return;
      injectEmmaFormsLayout(embed);
      if (embed.dataset.emmaFormsLayoutObserver === 'true') return;
      embed.dataset.emmaFormsLayoutObserver = 'true';

      var observer = new MutationObserver(function () {
        injectEmmaFormsLayout(embed);
        if (embed.shadowRoot && embed.dataset.emmaFormsShadowObserved !== 'true') {
          embed.dataset.emmaFormsShadowObserved = 'true';
          observer.observe(embed.shadowRoot, { childList: true, subtree: true });
        }
      });
      observer.observe(embed, { childList: true, subtree: true });

      if (embed.shadowRoot) {
        embed.dataset.emmaFormsShadowObserved = 'true';
        observer.observe(embed.shadowRoot, { childList: true, subtree: true });
      }
    });
  }

  document.addEventListener('DOMContentLoaded', bindEmmaFormsLayout);
  document.addEventListener('shopify:section:load', bindEmmaFormsLayout);
  setTimeout(bindEmmaFormsLayout, 500);
  setTimeout(bindEmmaFormsLayout, 1500);

  /* ------------------------------------------------------------------ */
  /* Free gift auto-add — locked qty=1, no remove, until stock runs out  */
  /* ------------------------------------------------------------------ */
  (function emmaFreeGiftSync() {
    var syncing = false;
    var queued = false;

    function cfg() {
      return window.emmaFreeGift || null;
    }

    function isWithinSchedule() {
      var c = cfg();
      if (!c || !c.enabled) return false;
      var now = Date.now();
      if (c.startAt) {
        var start = Date.parse(c.startAt);
        if (!isNaN(start) && now < start) return false;
      }
      if (c.endAt) {
        var end = Date.parse(c.endAt);
        if (!isNaN(end) && now > end) return false;
      }
      return true;
    }

    function routes() {
      var r = (window.themeVariables && window.themeVariables.routes) || {};
      return {
        cart: (r.cartUrl || '/cart') + '.js',
        add: (r.cartAddUrl || '/cart/add') + '.js',
        change: (r.cartChangeUrl || '/cart/change') + '.js'
      };
    }

    function isGiftLine(item) {
      var c = cfg();
      if (!c || !item) return false;
      var props = item.properties || {};
      if (c.propertyKey && String(props[c.propertyKey]) === String(c.propertyValue)) return true;
      if (c.productId != null && String(item.product_id) === String(c.productId)) return true;
      if (c.variantId != null && String(item.variant_id) === String(c.variantId)) return true;
      return false;
    }

    function findGiftLine(cart) {
      if (!cart || !cart.items) return null;
      for (var i = 0; i < cart.items.length; i++) {
        if (isGiftLine(cart.items[i])) return cart.items[i];
      }
      return null;
    }

    /** Paid-items subtotal in cents (excludes gift line). */
    function qualifyingSubtotal(cart) {
      if (!cart || !cart.items) return 0;
      var total = 0;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (isGiftLine(item)) continue;
        total += Number(item.final_line_price) || 0;
      }
      return total;
    }

    function meetsMinSubtotal(cart) {
      var c = cfg();
      if (!c) return false;
      var min = Number(c.minSubtotalCents);
      if (!min || min <= 0) {
        if (!cart || !cart.items) return false;
        return cart.items.some(function (item) {
          return !isGiftLine(item);
        });
      }
      return qualifyingSubtotal(cart) >= min;
    }

    function refreshCartUi() {
      // Main cart page does not AJAX-rerender line items ÃÂ¢ÃÂÃÂ reload so gift add/remove shows.
      var pageType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType;
      if (pageType === 'cart') {
        window.location.reload();
        return;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    async function fetchCart() {
      var res = await fetch(routes().cart, { credentials: 'same-origin', cache: 'no-store' });
      return res.json();
    }

    async function addGift() {
      var c = cfg();
      if (!c || !c.variantId) return null;
      // Theme settings store IDs as text; cart/add.js expects a numeric variant id.
      var variantId = Number(c.variantId);
      if (!variantId) return null;
      var props = {};
      props[c.propertyKey] = c.propertyValue;
      var res = await fetch(routes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: props }]
        })
      });
      if (!res.ok) {
        // Likely OOS or sold out ÃÂ¢ÃÂÃÂ treat as unavailable for this session.
        if (window.emmaFreeGift) window.emmaFreeGift.available = false;
        return null;
      }
      return res.json();
    }

    async function changeLine(key, quantity) {
      var res = await fetch(routes().change, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!res.ok) throw new Error('cart change failed');
      return res.json();
    }

    async function sync() {
      var c = cfg();
      if (!c || !c.enabled || !c.variantId) return;
      if (syncing) {
        queued = true;
        return;
      }

      syncing = true;
      var changed = false;
      var active = isWithinSchedule();

      try {
        // Always refetch ÃÂ¢ÃÂÃÂ event.detail.cart is often stale right after add-to-cart.
        var cart = await fetchCart();
        var gift = findGiftLine(cart);
        var qualifies = meetsMinSubtotal(cart);

        if (!active) {
          // Outside promo window: strip gift if present, never auto-add.
          if (gift) {
            await changeLine(gift.key, 0);
            changed = true;
          }
        } else if (!qualifies) {
          // Below ÃÂ¢ÃÂÃÂ¬50 (or empty): strip gift if present.
          if (gift) {
            await changeLine(gift.key, 0);
            changed = true;
          }
        } else if (!gift) {
          if (c.available !== false) {
            var added = await addGift();
            if (added) changed = true;
          }
        } else if (gift.quantity !== 1) {
          await changeLine(gift.key, 1);
          changed = true;
        }
      } catch (e) {
        /* ignore network / abort */
      } finally {
        syncing = false;
        if (queued) {
          queued = false;
          sync();
          return;
        }
        if (changed) refreshCartUi();
      }
    }

    function scheduleSync(delayMs) {
      setTimeout(function () {
        sync();
      }, delayMs || 0);
    }

    function bindGiftUiGuards(root) {
      var scope = root || document;
      scope.querySelectorAll('[data-emma-free-gift="true"]').forEach(function (line) {
        line.querySelectorAll(
          '.line-item__remove-button, .quantity-selector__button, .quantity-selector__input'
        ).forEach(function (el) {
          if (el.dataset.emmaGiftGuard === 'true') return;
          el.dataset.emmaGiftGuard = 'true';
          el.addEventListener(
            'click',
            function (ev) {
              ev.preventDefault();
              ev.stopPropagation();
              scheduleSync(50);
            },
            true
          );
          if (el.tagName === 'INPUT') {
            el.addEventListener(
              'change',
              function (ev) {
                ev.preventDefault();
                ev.stopPropagation();
                scheduleSync(50);
              },
              true
            );
          }
        });
      });
    }

    // Theme dispatches on documentElement; listen there + document.
    document.documentElement.addEventListener('cart:updated', function () {
      scheduleSync(150);
    });
    document.documentElement.addEventListener('variant:added', function () {
      scheduleSync(250);
    });
    document.addEventListener('cart:updated', function () {
      scheduleSync(150);
    });
    document.addEventListener('variant:added', function () {
      scheduleSync(250);
    });
    document.addEventListener('DOMContentLoaded', function () {
      bindGiftUiGuards(document);
      scheduleSync(100);
    });
    document.addEventListener('line-item-quantity:change:end', function () {
      scheduleSync(200);
    });
    document.addEventListener('shopify:section:load', function (event) {
      bindGiftUiGuards(event.target || document);
    });

    // Re-bind guards after mini-cart AJAX refresh.
    var drawer = document.getElementById('mini-cart');
    if (drawer) {
      new MutationObserver(function () {
        bindGiftUiGuards(drawer);
      }).observe(drawer, { childList: true, subtree: true });
    }
  })();

  function syncEmmaHeaderCartCount(count) {
    if (count == null) return;
    document.querySelectorAll('cart-count.emma-header__cart-count').forEach(function (el) {
      el.setAttribute('data-cart-count', String(count));
      el.textContent = String(count);
    });
  }

  document.addEventListener('cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart && cart.item_count != null) syncEmmaHeaderCartCount(cart.item_count);
  });
  document.addEventListener('cart:refresh', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart && cart.item_count != null) syncEmmaHeaderCartCount(cart.item_count);
  });
})();

(function emmaLocalizeEnHrefs() {
  var lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
  if (lang === 'en' || lang.indexOf('en-') === 0) return;

  function localize(href) {
    if (!href) return href;
    var trimmed = String(href).trim();
    if (trimmed === '/en' || trimmed === '/en/') return '/';
    return trimmed.replace(/\/en\//g, '/');
  }

  function rewrite(el, attr) {
    var val = el.getAttribute(attr);
    if (!val || val.indexOf('/en') === -1) return;
    var next = localize(val);
    if (next !== val) el.setAttribute(attr, next);
  }

  function run(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[href*="/en"]').forEach(function (el) { rewrite(el, 'href'); });
    root.querySelectorAll('[action*="/en"]').forEach(function (el) { rewrite(el, 'action'); });
  }

  run(document);
  document.addEventListener('shopify:section:load', function (event) { run(event.target); });
  document.addEventListener('emma:product-cards-updated', function () { run(document); });
})();
/* Article collection carousels Ã¢ÂÂ ensure Flickity mounts (imagesLoaded can stall) */
(function emmaArticleCarousels() {
  function nudge(root) {
    (root || document).querySelectorAll(
      '.emma-article-carousel flickity-carousel, flickity-carousel.emma-product-card-row__carousel, flickity-carousel.emma-best-sellers__carousel, flickity-carousel.emma-tiktok-reels__carousel'
    ).forEach(function (el) {
      if (el.classList.contains('flickity-enabled')) return;
      if (typeof el.reload === 'function') {
        try { el.reload(); } catch (e) {}
      }
    });
  }

  function boot() {
    nudge(document);
    requestAnimationFrame(function () { nudge(document); });
    setTimeout(function () { nudge(document); }, 400);
    setTimeout(function () { nudge(document); }, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (event) {
    nudge(event.target || document);
  });
})();
